import { Controller } from "@hotwired/stimulus"

// Generic 2-axis pane splitter for the corner workbench. Adapted from
// resizable_sidebar_controller: drag to resize, double-click to reset, the size
// persisted to localStorage and restored on connect(). Unlike the sidebar
// controller (which sizes its OWN element), this divider sits BETWEEN two flush
// panes and sizes a SIBLING pane referenced by `targetSelector` — the map pane
// (axis "x" → width) or the top row (axis "y" → height).
//
//   <div data-controller="pane-splitter"
//        data-pane-splitter-axis-value="x"
//        data-pane-splitter-storage-key-value="cw:mapW"
//        data-pane-splitter-default-value="560"
//        data-pane-splitter-min-value="340"
//        data-pane-splitter-max-value="1200"
//        data-pane-splitter-sibling-min-value="300"
//        data-pane-splitter-target-selector-value="#cw_map_pane">
//     <div data-pane-splitter-target="handle"></div>
//   </div>
//
// axis "x" sizes the sibling's width and clamps the max so the OTHER pane keeps
// at least `siblingMin` px (the workspace can be any width). axis "y" sizes the
// sibling's height between [min, max]. The handle visual is CSS-driven
// (.cw-splitter-handle in application.css); JS only owns geometry + persistence.

export default class extends Controller {
  static targets = ["handle"]
  static values = {
    axis: { type: String, default: "x" },
    storageKey: { type: String, default: "cw:pane" },
    default: { type: Number, default: 560 },
    min: { type: Number, default: 200 },
    max: { type: Number, default: 0 }, // 0 = no fixed max (axis x clamps via siblingMin)
    siblingMin: { type: Number, default: 300 },
    targetSelector: { type: String, default: "" },
  }

  connect() {
    this._onPointerDown = this._onPointerDown.bind(this)
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
    this._onKeyDown = this._onKeyDown.bind(this)
    this._onDblClick = this._onDblClick.bind(this)

    if (this.hasHandleTarget) {
      this.handleTarget.addEventListener("pointerdown", this._onPointerDown)
      this.handleTarget.addEventListener("dblclick", this._onDblClick)
      this.handleTarget.addEventListener("keydown", this._onKeyDown)
      this.handleTarget.setAttribute("tabindex", "0")
      this.handleTarget.setAttribute("role", "separator")
      this.handleTarget.setAttribute(
        "aria-orientation",
        this._isX ? "vertical" : "horizontal",
      )
      this.handleTarget.setAttribute("aria-label", "Resize pane")
    }

    this._applySize(this._storedSize())
  }

  disconnect() {
    if (this.hasHandleTarget) {
      this.handleTarget.removeEventListener("pointerdown", this._onPointerDown)
      this.handleTarget.removeEventListener("dblclick", this._onDblClick)
      this.handleTarget.removeEventListener("keydown", this._onKeyDown)
    }
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    this._releaseBodyCursor()
  }

  // ───── geometry helpers ─────

  get _isX() {
    return this.axisValue === "x"
  }

  get _pane() {
    if (!this.targetSelectorValue) return null
    // Scope to the workspace so multiple workbenches don't collide.
    const root = this.element.closest("[data-pane-splitter-root]") || document
    return root.querySelector(this.targetSelectorValue)
  }

  // Upper clamp. For axis x we keep the OTHER pane at least siblingMin wide
  // (workspace width − sibling reserve); for axis y we honour the fixed max.
  _maxSize() {
    if (this._isX) {
      const ws = this.element.closest("[data-pane-splitter-root]")
      const total = ws ? ws.clientWidth : 0
      const cap = total ? total - this.siblingMinValue : this.defaultValue * 2
      return Math.max(this.minValue, cap)
    }
    return this.maxValue > 0 ? this.maxValue : this.defaultValue * 2
  }

  // ───── pointer ─────

  _onPointerDown(e) {
    if (e.button !== 0) return
    e.preventDefault()
    this._dragStart = this._isX ? e.clientX : e.clientY
    const pane = this._pane
    this._dragStartSize = pane
      ? this._isX
        ? pane.getBoundingClientRect().width
        : pane.getBoundingClientRect().height
      : this.defaultValue
    document.body.style.cursor = this._isX ? "col-resize" : "row-resize"
    document.body.style.userSelect = "none"
    this.element.classList.add("is-resizing")
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
  }

  _onPointerMove(e) {
    const pos = this._isX ? e.clientX : e.clientY
    this._applySize(
      this._clamp(this._dragStartSize + (pos - this._dragStart)),
      { persist: false },
    )
  }

  _onPointerUp() {
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    this._releaseBodyCursor()
    this.element.classList.remove("is-resizing")
    this._persistSize(this._currentSize())
  }

  _onDblClick() {
    this._applySize(this.defaultValue)
  }

  _onKeyDown(e) {
    let delta = 0
    if (this._isX) {
      if (e.key === "ArrowLeft") delta = -1
      else if (e.key === "ArrowRight") delta = +1
    } else {
      if (e.key === "ArrowUp") delta = -1
      else if (e.key === "ArrowDown") delta = +1
    }
    if (!delta) return
    e.preventDefault()
    const step = e.shiftKey ? 32 : 8
    this._applySize(this._clamp(this._currentSize() + delta * step))
  }

  // ───── size plumbing ─────

  _currentSize() {
    const pane = this._pane
    if (!pane) return this.defaultValue
    const r = pane.getBoundingClientRect()
    return this._isX ? r.width : r.height
  }

  _applySize(size, { persist = true } = {}) {
    const pane = this._pane
    if (pane)
      pane.style[this._isX ? "width" : "height"] = `${Math.round(size)}px`
    if (persist) this._persistSize(size)
  }

  _persistSize(size) {
    try {
      localStorage.setItem(this.storageKeyValue, String(Math.round(size)))
    } catch (_) {
      /* private mode etc. — size is just non-sticky */
    }
  }

  _storedSize() {
    try {
      const n = parseInt(localStorage.getItem(this.storageKeyValue), 10)
      return Number.isFinite(n) ? this._clamp(n) : this.defaultValue
    } catch (_) {
      return this.defaultValue
    }
  }

  _clamp(size) {
    return Math.min(this._maxSize(), Math.max(this.minValue, size))
  }

  _releaseBodyCursor() {
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }
}
